import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { getMembers } from "../../services/api";
import MemberViewModal from "./MemberViewModal";
import MemberEditModal from "./MemberEditModal";

const uniqueValues = (rows, key, fallback = "-") => {
  const vals = rows.map((r) => r[key] || fallback).filter(Boolean);
  return [...new Set(vals)].sort((a, b) => String(a).localeCompare(String(b)));
};

const MemberList = forwardRef((props, ref) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const [search, setSearch] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [jimboFilter, setJimboFilter] = useState("");
  const [parokiaFilter, setParokiaFilter] = useState("");
  const [kandaFilter, setKandaFilter] = useState("");
  const [jumuiaFilter, setJumuiaFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [leaderFilter, setLeaderFilter] = useState("");
  const [editMode, setEditMode] = useState(false)

  /* PAGINATION */
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useImperativeHandle(ref, () => ({
    reloadMembers() {
      loadMembers();
    },
  }));

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const res = await getMembers();
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Load members error:", err);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const districtOptions = useMemo(() => uniqueValues(members, "district_name"), [members]);
  const jimboOptions = useMemo(() => uniqueValues(members, "jimbo_name"), [members]);
  const parokiaOptions = useMemo(() => uniqueValues(members, "parokia_name"), [members]);
  const kandaOptions = useMemo(() => uniqueValues(members, "kanda_name"), [members]);
  const jumuiaOptions = useMemo(() => uniqueValues(members, "jumuia_name"), [members]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      const matchesSearch =
        !q ||
        m.full_name?.toLowerCase().includes(q) ||
        m.member_code?.toLowerCase().includes(q) ||
        m.phone?.toLowerCase().includes(q) ||
        m.jumuia_name?.toLowerCase().includes(q) ||
        m.kanda_name?.toLowerCase().includes(q);

      const matchesDistrict = !districtFilter || (m.district_name || "-") === districtFilter;
      const matchesJimbo = !jimboFilter || (m.jimbo_name || "-") === jimboFilter;
      const matchesParokia = !parokiaFilter || (m.parokia_name || "-") === parokiaFilter;
      const matchesKanda = !kandaFilter || (m.kanda_name || "-") === kandaFilter;
      const matchesJumuia = !jumuiaFilter || (m.jumuia_name || "-") === jumuiaFilter;
      const matchesStatus = !statusFilter || (m.status || "").toUpperCase() === statusFilter;
      const matchesLeader =
        !leaderFilter ||
        (leaderFilter === "YES" && Number(m.is_jumuia_leader) === 1) ||
        (leaderFilter === "NO" && Number(m.is_jumuia_leader) === 0);

      return (
        matchesSearch &&
        matchesDistrict &&
        matchesJimbo &&
        matchesParokia &&
        matchesKanda &&
        matchesJumuia &&
        matchesStatus &&
        matchesLeader
      );
    });
  }, [
    members,
    search,
    districtFilter,
    jimboFilter,
    parokiaFilter,
    kandaFilter,
    jumuiaFilter,
    statusFilter,
    leaderFilter,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    districtFilter,
    jimboFilter,
    parokiaFilter,
    kandaFilter,
    jumuiaFilter,
    statusFilter,
    leaderFilter,
  ]);

  const totalPages = Math.ceil(filtered.length / rowsPerPage);

  const paginatedMembers = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, currentPage, rowsPerPage]);

  const clearFilters = () => {
    setSearch("");
    setDistrictFilter("");
    setJimboFilter("");
    setParokiaFilter("");
    setKandaFilter("");
    setJumuiaFilter("");
    setStatusFilter("");
    setLeaderFilter("");
  };

  return (
    <div className="transactions-page">
      {/* PAGE HEADER */}
<div className="transactions-page-header">
  {/* <h3>Available Members</h3> */}
  <h3></h3>
  <div className="transactions-header-actions">
    <button className="btn" onClick={clearFilters}>Clear Filters</button>
  </div>
</div>
{/* FILTERS */}
<div className="card transaction-filter-card">
  <div className="transaction-filter-grid">
    <select className="input" value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)}>
      <option value="">--Choose District--</option>
      {districtOptions.map((item) => <option key={item} value={item}>{item}</option>)}
    </select>

    <select className="input" value={jimboFilter} onChange={(e) => setJimboFilter(e.target.value)}>
      <option value="">--Choose Jimbo--</option>
      {jimboOptions.map((item) => <option key={item} value={item}>{item}</option>)}
    </select>

    <select className="input" value={parokiaFilter} onChange={(e) => setParokiaFilter(e.target.value)}>
      <option value="">--Choose Parokia--</option>
      {parokiaOptions.map((item) => <option key={item} value={item}>{item}</option>)}
    </select>

    <select className="input" value={kandaFilter} onChange={(e) => setKandaFilter(e.target.value)}>
      <option value="">--Choose Kanda--</option>
      {kandaOptions.map((item) => <option key={item} value={item}>{item}</option>)}
    </select>

    <select className="input" value={jumuiaFilter} onChange={(e) => setJumuiaFilter(e.target.value)}>
      <option value="">--Choose Jumuiya--</option>
      {jumuiaOptions.map((item) => <option key={item} value={item}>{item}</option>)}
    </select>

    <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
      <option value="">Select Status</option>
      <option value="ACTIVE">ACTIVE</option>
      <option value="INACTIVE">INACTIVE</option>
    </select>

    <select className="input" value={leaderFilter} onChange={(e) => setLeaderFilter(e.target.value)}>
      <option value="">--Jumuiya Leader--</option>
      <option value="YES">Yes</option>
      <option value="NO">No</option>
    </select>

    <input
      className="input"
      placeholder="Search..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
    />
  </div>
</div>

      {/* MEMBER CARDS TABLE */}
      <div className="transaction-table-wrap">
        {loading ? (
          <div className="spinner-wrap"><div className="spinner" /></div>
        ) : (
          <table className="transaction-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Member Code</th>
                <th>Details</th>
                <th>Locations</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedMembers.length === 0 && (
                <tr>
                  <td colSpan="6" className="empty">No members found</td>
                </tr>
              )}
              {paginatedMembers.map((m, i) => (
                <tr key={m.member_id}>
                  <td className="transaction-id-cell">{(currentPage - 1) * rowsPerPage + i + 1}</td>
                  <td>{m.member_code || "-"}</td>
                  <td>
                    <div className="detail-line"><strong>Name:</strong> {m.full_name}</div>
                    <div className="detail-line"><strong>Gender:</strong> {m.gender || "-"}</div>
                    <div className="detail-line"><strong>Phone:</strong> {m.phone || "-"}</div>
                    <div className="detail-line"><strong>Backup:</strong> {m.backup_phone || "-"}</div>
                    <div className="detail-line"><strong>Marital Status:</strong> {m.marital_status || "-"}</div>
                  </td>
                  <td>
                    <div className="detail-line"><strong>Jimbo:</strong> {m.jimbo_name || "-"}</div>
                    <div className="detail-line"><strong>Parokia:</strong> {m.parokia_name || "-"}</div>
                    <div className="detail-line"><strong>Kanda:</strong> {m.kanda_name || "-"}</div>
                    <div className="detail-line"><strong>Jumuiya:</strong> {m.jumuia_name || "-"}</div>
                    <div className="detail-line"><strong>Region:</strong> {m.region_name || "-"}</div>
                    <div className="detail-line"><strong>District:</strong> {m.district_name || "-"}</div>
                  </td>
                  <td className="transaction-action-cell">
                    <span className={`status ${m.status?.toLowerCase()}`}>{m.status}</span>
                  </td>
                  <td className="transaction-action-cell">
                    <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn dark"
                      onClick={() => {
                        setEditMode(false);
                        setSelected(m);
                      }}
                    >
                      View
                    </button>

                    <button
                      className="btn warning"
                      onClick={() => {
                        setEditMode(true);
                        setSelected(m);
                      }}
                      title="Edit Member"
                    >
                      ✏️
                    </button>
                  </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* PAGINATION */}
      <div className="pagination-controls">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span>Rows:</span>
          <select className="input small" value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
            {[10, 20, 50, 100, 500].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn small" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>◀</button>
          <span>Page {currentPage} of {totalPages || 1}</span>
          <button className="btn small" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>▶</button>
        </div>
        <div>Total: {filtered.length}</div>
      </div>

      {/* {selected && <MemberViewModal member={selected} onClose={() => setSelected(null)} />} */}
      {selected && !editMode && (
        <MemberViewModal
          member={selected}
          onClose={() => {
            setSelected(null);
          }}
          onEdit={() => {
            setEditMode(true);
          }}
        />
      )}

      {selected && editMode && (
        <MemberEditModal
          member={selected}
          onClose={() => {
            setSelected(null);
            setEditMode(false);
          }}
          onUpdated={() => {
            setSelected(null);
            setEditMode(false);
            loadMembers();
          }}
        />
      )}
    </div>
  );
});

export default MemberList;